/**
 * phase-template-variable-bindings — bind ONE template variable to a source.
 *
 * Opened from the provenance panel (design item 6: the panel is the entry
 * point). The source lists come from the server, which derives them from the
 * REAL resolvers — so this picker can never offer a field that cannot resolve.
 *
 * A binding is an OVERRIDE of the built-in default, so the modal also offers
 * Reset ("use the built-in default again") whenever one is already in force.
 */
import { MovableDialog } from '@/components/shared/MovableDialog';
import { useEffect, useState, type ReactElement } from 'react';
import {
  v2,
  type BindableSources,
  type BindingHealthApi,
  type BindingSourceKind,
  type ContactAttribute,
  type ContactFunction,
  type ContactRegionMode,
  type SetVariableBindingBody,
  type VariableBindingApi,
} from '@/api/v2Client';
import { formatApiError } from '@/lib/errors';
import { BindingStatusBadge } from './BindingStatusBadge';

const KIND_LABEL: Record<BindingSourceKind, string> = {
  PROFILE_FIELD: 'Company profile field',
  SUBMISSION_FIELD: 'Submission field',
  CONTACT: 'Contact (by function)',
  SIGNATORY: 'Signatory (by function)',
  LITERAL: 'Fixed text',
};

const REGION_MODE_LABEL: Record<ContactRegionMode, string> = {
  template: "This template's region",
  submission: "The submission's region",
  explicit: 'A specific region…',
};

export interface VariableBindingModalProps {
  readonly templateId: string;
  readonly variableName: string;
  /** The binding already in force, if any (drives Reset + initial values). */
  readonly existing: VariableBindingApi | null;
  readonly onClose: () => void;
  /** Called after a successful save or reset so the panel can refresh. */
  readonly onSaved: () => void;
  /**
   * Form Templates row 6 — the profile the provenance panel is checking
   * against; the live check evaluates the draft binding against it.
   */
  readonly profileId?: string | null;
}

export function VariableBindingModal({
  templateId,
  variableName,
  existing,
  onClose,
  onSaved,
  profileId = null,
}: VariableBindingModalProps): ReactElement {
  const [sources, setSources] = useState<BindableSources | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [kind, setKind] = useState<BindingSourceKind>(existing?.sourceKind ?? 'PROFILE_FIELD');
  const [field, setField] = useState<string>(existing?.sourceField ?? '');
  const [fn, setFn] = useState<ContactFunction>(existing?.contactFunction ?? 'general');
  const [regionMode, setRegionMode] = useState<ContactRegionMode>(
    existing?.contactRegionMode ?? 'template',
  );
  const [region, setRegion] = useState<string>(existing?.contactRegion ?? '');
  const [attr, setAttr] = useState<ContactAttribute>(existing?.contactAttribute ?? 'name');
  const [literal, setLiteral] = useState<string>(existing?.literalValue ?? '');

  useEffect(() => {
    let cancelled = false;
    v2.documentTemplates
      .bindableSources()
      .then((s) => {
        if (!cancelled) setSources(s);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(formatApiError(e, 'Could not load the list of sources.'));
      });
    return (): void => {
      cancelled = true;
    };
  }, []);

  const fieldOptions =
    kind === 'PROFILE_FIELD'
      ? (sources?.profileFields ?? [])
      : kind === 'SUBMISSION_FIELD'
        ? (sources?.submissionFields ?? [])
        : [];

  /** The binding as the editor currently has it (what Save sends). */
  const draftBody = (): SetVariableBindingBody => {
    const body: SetVariableBindingBody = { sourceKind: kind };
    if (kind === 'PROFILE_FIELD' || kind === 'SUBMISSION_FIELD') body.sourceField = field;
    if (kind === 'CONTACT' || kind === 'SIGNATORY') {
      body.contactFunction = fn;
      body.contactRegionMode = regionMode;
      body.contactAttribute = attr;
      if (regionMode === 'explicit') body.contactRegion = region;
    }
    if (kind === 'LITERAL') body.literalValue = literal;
    return body;
  };

  // Row 6 — live check: as the draft changes, ask the server (the one health
  // evaluator) whether it resolves for the selected profile. Debounced.
  const [health, setHealth] = useState<BindingHealthApi | null>(null);
  useEffect(() => {
    let cancelled = false;
    const t = globalThis.setTimeout(() => {
      v2.documentTemplates
        .checkVariableBinding(templateId, variableName, {
          ...draftBody(),
          ...(profileId ? { profileId } : {}),
        })
        .then((h) => {
          if (!cancelled) setHealth(h);
        })
        .catch(() => {
          if (!cancelled) setHealth(null);
        });
    }, 250);
    return (): void => {
      cancelled = true;
      globalThis.clearTimeout(t);
    };
    // draftBody reads exactly these inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateId, variableName, profileId, kind, field, fn, regionMode, region, attr, literal]);

  const save = async (): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      await v2.documentTemplates.setVariableBinding(templateId, variableName, draftBody());
      onSaved();
    } catch (e) {
      setError(formatApiError(e, 'Could not save this binding.'));
      setSaving(false);
    }
  };

  const reset = async (): Promise<void> => {
    setSaving(true);
    setError(null);
    try {
      await v2.documentTemplates.resetVariableBinding(templateId, variableName);
      onSaved();
    } catch (e) {
      setError(formatApiError(e, 'Could not reset this binding.'));
      setSaving(false);
    }
  };

  const labelStyle = { fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 };
  const inputStyle = {
    width: '100%',
    padding: '6px 8px',
    border: '1px solid var(--border, #d1d5db)',
    borderRadius: 6,
    fontSize: 13,
    boxSizing: 'border-box' as const,
  };

  // phase-movable-dialogs — shared movable shell.
  return (
    <MovableDialog
      persistKey="formTemplates.variableBinding"
      title={<>Bind «{variableName}»</>}
      ariaLabel={`Bind ${variableName}`}
      testId="variable-binding-modal"
      onClose={onClose}
      closeOnBackdrop
      width="min(520px, 92vw)"
      maxHeight="85vh"
      zIndex={1100}
    >
      <>
        <p style={{ margin: '0 0 16px', fontSize: 12, color: 'var(--text-muted, #6b7280)' }}>
          Choose where this variable&apos;s value comes from. This overrides the built-in
          default for your organisation only.
        </p>

        <label style={labelStyle} htmlFor="binding-kind">
          Source type
        </label>
        <select
          id="binding-kind"
          data-testid="binding-kind"
          value={kind}
          onChange={(e) => setKind(e.target.value as BindingSourceKind)}
          style={{ ...inputStyle, marginBottom: 12 }}
        >
          {(sources?.sourceKinds ?? (Object.keys(KIND_LABEL) as BindingSourceKind[])).map((k) => (
            <option key={k} value={k}>
              {KIND_LABEL[k]}
            </option>
          ))}
        </select>

        {(kind === 'PROFILE_FIELD' || kind === 'SUBMISSION_FIELD') && (
          <>
            <label style={labelStyle} htmlFor="binding-field">
              Field
            </label>
            <select
              id="binding-field"
              data-testid="binding-field"
              value={field}
              onChange={(e) => setField(e.target.value)}
              style={{ ...inputStyle, marginBottom: 12 }}
            >
              <option value="">Choose a field…</option>
              {fieldOptions.map((f) => (
                <option key={f.key} value={f.key}>
                  {f.label} ({f.key})
                </option>
              ))}
            </select>
          </>
        )}

        {(kind === 'CONTACT' || kind === 'SIGNATORY') && (
          <>
            <label style={labelStyle} htmlFor="binding-function">
              Function
            </label>
            <select
              id="binding-function"
              data-testid="binding-function"
              value={fn}
              onChange={(e) => setFn(e.target.value as ContactFunction)}
              style={{ ...inputStyle, marginBottom: 12 }}
            >
              {(sources?.contactFunctions ?? []).map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>

            <label style={labelStyle} htmlFor="binding-region-mode">
              Region
            </label>
            <select
              id="binding-region-mode"
              data-testid="binding-region-mode"
              value={regionMode}
              onChange={(e) => setRegionMode(e.target.value as ContactRegionMode)}
              style={{ ...inputStyle, marginBottom: 12 }}
            >
              {(sources?.contactRegionModes ?? []).map((m) => (
                <option key={m} value={m}>
                  {REGION_MODE_LABEL[m]}
                </option>
              ))}
            </select>
            {regionMode === 'explicit' && (
              <input
                data-testid="binding-region"
                value={region}
                onChange={(e) => setRegion(e.target.value)}
                placeholder="e.g. FDA"
                style={{ ...inputStyle, marginBottom: 12 }}
              />
            )}

            <label style={labelStyle} htmlFor="binding-attribute">
              Which detail
            </label>
            <select
              id="binding-attribute"
              data-testid="binding-attribute"
              value={attr}
              onChange={(e) => setAttr(e.target.value as ContactAttribute)}
              style={{ ...inputStyle, marginBottom: 12 }}
            >
              {(sources?.contactAttributes ?? [])
                .filter((a) => !(kind === 'SIGNATORY' && a === 'phone'))
                .map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
            </select>
          </>
        )}

        {kind === 'LITERAL' && (
          <>
            <label style={labelStyle} htmlFor="binding-literal">
              Fixed text
            </label>
            <textarea
              id="binding-literal"
              data-testid="binding-literal"
              value={literal}
              onChange={(e) => setLiteral(e.target.value)}
              rows={3}
              style={{ ...inputStyle, marginBottom: 12, fontFamily: 'inherit' }}
            />
          </>
        )}

        {/* Row 6 — does this binding actually produce a value? */}
        <div data-testid="binding-live-check" style={{ marginBottom: 10, fontSize: 12 }}>
          <span style={{ color: 'var(--text-muted, #6b7280)' }}>
            {profileId ? 'Check against the selected profile:' : 'Check (select a profile in the panel to test values):'}
          </span>
          {health ? (
            <BindingStatusBadge
              testId="binding-live-status"
              status={health.status}
              reason={health.reason}
              preview={health.resolvedPreview}
            />
          ) : (
            <span style={{ display: 'block', color: 'var(--text-muted, #6b7280)' }}>Checking…</span>
          )}
        </div>

        {error && (
          <div role="alert" style={{ color: '#b91c1c', fontSize: 12, marginBottom: 10 }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginTop: 8 }}>
          <div>
            {existing && (
              <button
                type="button"
                data-testid="binding-reset-btn"
                onClick={() => void reset()}
                disabled={saving}
                title="Remove this binding and use the built-in default again"
                style={{
                  border: '1px solid #b45309',
                  color: '#b45309',
                  background: 'var(--surface, #fff)',
                  borderRadius: 6,
                  padding: '6px 12px',
                  fontSize: 12,
                  cursor: 'pointer',
                }}
              >
                Reset to default
              </button>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              data-testid="binding-cancel-btn"
              onClick={onClose}
              disabled={saving}
              style={{
                border: '1px solid var(--border, #d1d5db)',
                background: 'var(--surface, #fff)',
                borderRadius: 6,
                padding: '6px 14px',
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="binding-save-btn"
              onClick={() => void save()}
              disabled={saving}
              style={{
                border: 'none',
                background: 'var(--accent, #2563eb)',
                color: '#fff',
                borderRadius: 6,
                padding: '6px 14px',
                fontSize: 13,
                fontWeight: 600,
                cursor: saving ? 'not-allowed' : 'pointer',
                opacity: saving ? 0.6 : 1,
              }}
            >
              {saving ? 'Saving…' : 'Save binding'}
            </button>
          </div>
        </div>
      </>
    </MovableDialog>
  );
}
